import { useState, useCallback } from 'react';
import request from 'utils/request_ajax';

/**
 * @typedef AskTheExpertOptions
 * @type {Object}
 *
 * @property {string} endpoint The endpoint to which the request will be made.
 * @property {{ [param: string]: any }} include An object to include in the
 *   request. Use this to include additional params in the request body such as
 *   a the section id of a lesson.
 * @property {number} autoResetDelay The delay in millisecond before resetting
 *   the state after a successful submission.
 * @property {Function} onReset A callback function that will be called after
 *   the request is submitted successfully and state is reset.
 */

/**
 * A hook that holds the logic for Ask The Expert. This includes maintaining the
 * state of an ask the expert
 * @param {AskTheExpertOptions} options
 */
function useAskTheExpert({
  endpoint,
  include = {},
  autoResetDelay = 0,
  onReset,
} = {}) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');

  const reset = useCallback(() => {
    setSent(false);
    setMessage('');
    if (onReset) {
      onReset();
    }
  }, [onReset]);

  const submit = useCallback(() => {
    setSending(true);
    request
      .postJSON(endpoint, { message, ...include })
      .then(() => {
        setSending(false);
        setSent(true);
        if (autoResetDelay) {
          setTimeout(reset, autoResetDelay);
        }
      })
      .fail(() => setSending(false));
  }, [endpoint, include, message, reset, autoResetDelay]);

  return {
    submit,
    sent,
    sending,
    message,
    setMessage,
    reset,
  };
}

export default useAskTheExpert;
